export function shouldPlayProductVideo(state) {
  return Boolean(
    state.sectionVisible
      && state.documentVisible
      && state.muted
      && state.loop
      && state.playsInline
      && state.autoplay,
  );
}

function isMobileMedia() {
  return window.matchMedia('(max-width: 749px)').matches;
}

function syncVideoPoster(video) {
  if (!video) return;

  const host = video.closest('[data-responsive-video-poster]');
  if (!host) return;

  const poster = isMobileMedia()
    ? host.dataset.mobilePoster || host.dataset.poster || ''
    : host.dataset.poster || '';

  if (poster && video.getAttribute('poster') !== poster) {
    video.setAttribute('poster', poster);
  }
}

const elementName = 'aceii-product-media';

if (
  typeof window !== 'undefined'
  && typeof HTMLElement !== 'undefined'
  && !window.customElements.get(elementName)
) {
  class AceiiProductMedia extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;

      this.abortController = new AbortController();
      this.sectionVisible = false;
      this.documentVisible = !document.hidden;
      this.videos = [...this.querySelectorAll('[data-product-video], .aceii-product-story__video')];
      const { signal } = this.abortController;

      this.videos.forEach(syncVideoPoster);

      document.addEventListener(
        'visibilitychange',
        () => {
          this.documentVisible = !document.hidden;
          this.syncVideos();
        },
        { signal },
      );

      window.addEventListener(
        'resize',
        () => this.videos.forEach(syncVideoPoster),
        { signal },
      );

      this.observer = new IntersectionObserver(
        ([entry]) => {
          this.sectionVisible = Boolean(entry?.isIntersecting);
          this.syncVideos();
        },
        {
          rootMargin: '160px 0px',
          threshold: 0.01,
        },
      );

      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.pauseAll();
      this.observer?.disconnect();
      this.abortController?.abort();
      this.abortController = null;
    }

    ensureSource(video) {
      if (video.currentSrc || video.src) return;
      syncVideoPoster(video);
      let sourceAttached = false;

      if (video.dataset.src) {
        video.src = video.dataset.src;
        sourceAttached = true;
      }

      video.querySelectorAll('source[data-src]').forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
        sourceAttached = true;
      });

      if (sourceAttached) video.load();
    }

    syncVideos() {
      this.videos.forEach((video) => {
        const canPlay = shouldPlayProductVideo({
          sectionVisible: this.sectionVisible,
          documentVisible: this.documentVisible,
          muted: video.muted,
          loop: video.loop,
          playsInline: video.playsInline,
          autoplay: video.dataset.autoplay !== 'false',
        });

        if (!canPlay) {
          if (!video.paused) video.pause();
          return;
        }

        this.ensureSource(video);
        const playPromise = video.play();

        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {
            video.pause();
          });
        }
      });
    }

    pauseAll() {
      this.videos.forEach((video) => {
        if (!video.paused) video.pause();
      });
    }
  }

  window.customElements.define(elementName, AceiiProductMedia);
}
